import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSyncDuelSocket } from '../../contexts/SyncDuelSocketContext.tsx';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '@chakra-ui/react';
import { auth } from '../../config/firebase.ts';
import { useGame } from '../../contexts/GameContext.tsx';
import sfx from '../../lib/goofyAudio.ts';
import PlayerName from '../Common/PlayerName.tsx';
import './SyncDuelLobby.css';

export default function SyncDuelLobby() {
  const { code: routeCode } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { state, join, toggleReady, connected, leave, setConfig, socket } = useSyncDuelSocket() as any;
  const { fetchCategories, state: gameState } = useGame();

  const code = routeCode || state?.code || '';
  const name = useMemo(() => (auth.currentUser?.displayName || localStorage.getItem('syncduel:name') || ''), [auth.currentUser?.displayName]);
  const youAreJoined = !!state?.players?.some((p: any) => p.name === name);
  const youAreReady = !!state?.players?.find((p: any) => p.name === name)?.ready;
  const isHost = !!(state?.hostId && socket?.id && state.hostId === socket.id);
  const joinedOnceRef = useRef(false);

  const [selectedCategories, setSelectedCategories] = useState<string[]>(state?.categoryKeys || (state?.categoryKey ? [state.categoryKey] : []));
  const [catSearch, setCatSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [localMaxQuestions, setLocalMaxQuestions] = useState<number>(state?.total || 10);
  const [localTimePerQuestion, setLocalTimePerQuestion] = useState<number>((state as any)?.timePerQuestion || 10);
  const [categoryMode, setCategoryMode] = useState<'random' | 'fixed'>(state?.categoryKey || state?.categoryKeys?.length ? 'fixed' : 'random');
  const categoriesPerPage = 12;

  useEffect(() => {
    const n = auth.currentUser?.displayName;
    if (n) localStorage.setItem('syncduel:name', n);
  }, [auth.currentUser?.displayName]);

  useEffect(() => {
    if (connected && code && name && !joinedOnceRef.current) {
      joinedOnceRef.current = true;
      join(code, name, auth.currentUser?.uid);
      try { sfx.join(); } catch {}
    }
  }, [connected, code, name, join]);

  useEffect(() => {
    if (state?.inGame) navigate(`/syncduel/${code}/play`);
  }, [state?.inGame, code, navigate]);

  useEffect(() => {
    if (state && 'categoryKeys' in state) {
      setSelectedCategories(state.categoryKeys || []);
    } else if (state && 'categoryKey' in state && state.categoryKey) {
      setSelectedCategories([state.categoryKey]);
    }
  }, [state?.categoryKey, state?.categoryKeys]);

  useEffect(() => {
    setCategoryMode((state?.categoryKeys?.length || state?.categoryKey) ? 'fixed' : 'random');
  }, [state?.categoryKey, state?.categoryKeys]);

  useEffect(() => {
    const anyState: any = state as any;
    if (typeof anyState?.maxQuestions === 'number') setLocalMaxQuestions(anyState.maxQuestions || 10);
    else if (typeof state?.total === 'number') setLocalMaxQuestions(state.total || 10);
    if (typeof anyState?.timePerQuestion === 'number') setLocalTimePerQuestion(anyState.timePerQuestion || 10);
  }, [(state as any)?.maxQuestions, state?.total, (state as any)?.timePerQuestion]);

  const handleCopy = async () => {
    const url = `${window.location.origin}/syncduel/${code}`;
    try {
      await navigator.clipboard.writeText(url);
      toast({ title: 'Lien copié', description: url, status: 'success', duration: 2000 });
    } catch {
      toast({ title: 'Impossible de copier', status: 'error', duration: 2000 });
    }
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/syncduel/${code}`;
    if ((navigator as any).share) {
      try {
        await (navigator as any).share({ title: 'Rejoins mon Sync Duel', url });
      } catch {}
    } else {
      handleCopy();
    }
  };

  useEffect(() => {
    if (!gameState.categories?.length) {
      fetchCategories('fr').catch(() => {});
    }
  }, [fetchCategories, gameState.categories?.length]);

  const { paginatedCategories, totalPages, hasSearch } = useMemo(() => {
    const list = gameState.categories || [];
    const q = catSearch.trim().toLowerCase();
    let filtered = list;
    
    if (q) {
      filtered = list.filter((c: any) =>
        (c.name || '').toLowerCase().includes(q) || (c.id || '').toLowerCase().includes(q)
      );
    }
    
    const total = Math.ceil(filtered.length / categoriesPerPage);
    const start = currentPage * categoriesPerPage;
    const paginated = filtered.slice(start, start + categoriesPerPage);
    
    return { 
      paginatedCategories: paginated, 
      totalPages: total,
      hasSearch: !!q 
    };
  }, [gameState.categories, catSearch, currentPage, categoriesPerPage]);

  const handleCategoryToggle = (categoryId: string) => {
    if (!isHost) return;
    
    const newSelection = selectedCategories.includes(categoryId)
      ? selectedCategories.filter(id => id !== categoryId)
      : [...selectedCategories, categoryId];
    
    setSelectedCategories(newSelection);
    if (code) setConfig(code, { categoryKeys: newSelection } as any);
  };

  const getSelectedCategoryInfo = () => {
    if (selectedCategories.length === 0) return null;
    const categories = gameState.categories || [];
    return selectedCategories.map(id => categories.find((c: any) => c.id === id)).filter(Boolean);
  };

  return (
    <div className="syncduel-container">
      <div className="syncduel-card">
        <div className="syncduel-header">
          <h1 className="syncduel-title">⚡ Sync Duel</h1>
          <p className="syncduel-subtitle">Salon d'attente — invite un ami et prépare-toi</p>
        </div>

        {/* Code de lobby + actions */}
        <div className="syncduel-code-section">
          <div className="syncduel-code-display">
            <span className="syncduel-code-label">Code:</span>
            <span className="syncduel-code-value">{code || '------'}</span>
          </div>
          <div className="syncduel-code-actions">
            <button className="syncduel-btn syncduel-btn-primary" onClick={handleShare}>
              🔗 Partager
            </button>
            <button className="syncduel-btn syncduel-btn-icon" onClick={handleCopy} title="Copier le lien">
              📋
            </button>
          </div>
        </div>

        {/* Config: rules */}
        <div className="syncduel-config-section">
          <div className="syncduel-config-row">
            <div className="syncduel-config-group">
              <span className="syncduel-config-label">Nombre de questions:</span>
              <div className="syncduel-number-control">
                <button
                  className="syncduel-number-btn"
                  onClick={() => {
                    const n = Math.max(5, localMaxQuestions - 1);
                    setLocalMaxQuestions(n);
                    if (code && isHost) setConfig(code, { maxQuestions: n } as any);
                  }}
                  disabled={!!state?.inGame || !isHost || localMaxQuestions <= 5}
                >
                  −
                </button>
                <span className="syncduel-number-value">{localMaxQuestions}</span>
                <button
                  className="syncduel-number-btn"
                  onClick={() => {
                    const n = Math.min(50, localMaxQuestions + 1);
                    setLocalMaxQuestions(n);
                    if (code && isHost) setConfig(code, { maxQuestions: n } as any);
                  }}
                  disabled={!!state?.inGame || !isHost || localMaxQuestions >= 50}
                >
                  +
                </button>
              </div>
            </div>
            <div className="syncduel-config-group">
              <span className="syncduel-config-label">Temps par question:</span>
              <div className="syncduel-number-control">
                <button
                  className="syncduel-number-btn"
                  onClick={() => {
                    const t = Math.max(5, localTimePerQuestion - 1);
                    setLocalTimePerQuestion(t);
                    if (code && isHost) setConfig(code, { timePerQuestion: t } as any);
                  }}
                  disabled={!!state?.inGame || !isHost || localTimePerQuestion <= 5}
                >
                  −
                </button>
                <span className="syncduel-number-value">{localTimePerQuestion}s</span>
                <button
                  className="syncduel-number-btn"
                  onClick={() => {
                    const t = Math.min(30, localTimePerQuestion + 1);
                    setLocalTimePerQuestion(t);
                    if (code && isHost) setConfig(code, { timePerQuestion: t } as any);
                  }}
                  disabled={!!state?.inGame || !isHost || localTimePerQuestion >= 30}
                >
                  +
                </button>
              </div>
            </div>
            <div className="syncduel-config-group">
              <span className="syncduel-config-label">Catégorie:</span>
              <div className="syncduel-radio-group">
                <button
                  className={`syncduel-radio ${categoryMode === 'random' ? 'active' : ''}`}
                  onClick={() => {
                    if (!isHost || state?.inGame) return;
                    setCategoryMode('random');
                    setSelectedCategories([]);
                    if (code) {
                      setConfig(code, { categoryKeys: [] } as any);
                    }
                  }}
                  disabled={!isHost || !!state?.inGame}
                >
                  🎲 Aléatoire
                </button>
                <button
                  className={`syncduel-radio ${categoryMode === 'fixed' ? 'active' : ''}`}
                  onClick={() => {
                    if (!isHost || state?.inGame) return;
                    setCategoryMode('fixed');
                  }}
                  disabled={!isHost || !!state?.inGame}
                >
                  📌 Fixe
                </button>
              </div>
              {categoryMode === 'random' ? (
                <span className="syncduel-badge">🎲 Aléatoire</span>
              ) : (
                selectedCategories.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {getSelectedCategoryInfo()?.map((cat: any) => (
                      <span key={cat.id} className="syncduel-badge">
                        {(cat.emoji || '') + ' '}{cat.name}
                      </span>
                    ))}
                  </div>
                )
              )}
            </div>
          </div>
          {categoryMode === 'fixed' && (
            <>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px', alignItems: 'center' }}>
                <input
                  type="text"
                  className="syncduel-search-input"
                  placeholder="🔍 Rechercher… (histoire, science, sport…)"
                  value={catSearch}
                  onChange={(e) => { setCatSearch(e.target.value); setCurrentPage(0); }}
                />
                {selectedCategories.length > 0 && (
                  <span style={{ color: 'var(--texte-secondaire)', fontSize: '14px', whiteSpace: 'nowrap' }}>
                    {selectedCategories.length} sélectionnée{selectedCategories.length > 1 ? 's' : ''}
                  </span>
                )}
              </div>
              <div className="syncduel-category-chips">
                {paginatedCategories.map((c: any) => (
                  <button
                    key={c.id}
                    className={`syncduel-category-chip ${selectedCategories.includes(c.id) ? 'selected' : ''}`}
                    onClick={() => handleCategoryToggle(c.id)}
                    disabled={!isHost}
                  >
                    {selectedCategories.includes(c.id) && '✓ '}{(c.emoji || '') + ' '}{c.name}
                  </button>
                ))}
                {paginatedCategories.length === 0 && (
                  <p style={{ color: 'var(--texte-secondaire)', fontSize: '14px' }}>Aucune catégorie ne correspond</p>
                )}
              </div>
              {totalPages > 1 && (
                <div className="syncduel-pagination">
                  <button
                    className="syncduel-pagination-btn"
                    onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                    disabled={currentPage === 0 || !isHost}
                  >
                    ← Précédent
                  </button>
                  <span className="syncduel-pagination-info">
                    Page {currentPage + 1} / {totalPages}
                  </span>
                  <button
                    className="syncduel-pagination-btn"
                    onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                    disabled={currentPage >= totalPages - 1 || !isHost}
                  >
                    Suivant →
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Info utilisateur */}
        {!youAreJoined && (
          <div style={{ padding: '16px', background: 'rgba(139, 148, 179, 0.1)', borderRadius: '12px', color: 'var(--texte-secondaire)', marginBottom: '24px' }}>
            Connecté en tant que <strong>{name || '—'}</strong>. Connexion au salon…
          </div>
        )}

        {/* Joueurs */}
        <div className="syncduel-players-section">
          <h3 className="syncduel-section-title">👥 Joueurs</h3>
          <div className="syncduel-players-grid">
            {(state?.players || []).map((p: any, idx: number) => (
              <div key={p.id} className="syncduel-player-card">
                <div className="syncduel-player-info">
                  <div className="syncduel-player-avatar">
                    {(p.name || '?')[0].toUpperCase()}
                  </div>
                  <div className="syncduel-player-details">
                    <div className="syncduel-player-name">
                      <PlayerName
                        userId={p.uid || p.id}
                        userName={p.name + (p.name === name ? ' (vous)' : '')}
                        checkEffect={!!p.uid}
                      />
                    </div>
                    <div className="syncduel-player-stats-row">
                      <span className="syncduel-stat-item">Victoires: <strong>{p.stats?.totalWins ?? 0}</strong></span>
                      <span className="syncduel-stat-divider">•</span>
                      <span className="syncduel-stat-item">Face-à-face: <span className="syncduel-stat-wins"><strong>{p.stats?.winsVs ?? 0}</strong>V</span> - <span className="syncduel-stat-losses"><strong>{p.stats?.lossesVs ?? 0}</strong>D</span></span>
                    </div>
                  </div>
                </div>
                <div className={`syncduel-player-status ${p.ready ? 'ready' : 'waiting'}`}>
                  {p.ready ? '✅ Prêt' : '⏳ En attente'}
                </div>
              </div>
            ))}
            {((state?.players || []).length) < 2 && (
              <div className="syncduel-waiting-player">
                ⏳ En attente d'un second joueur…
              </div>
            )}
          </div>
        </div>

        {/* Actions de readiness */}
        <div>
          <button
            className={`syncduel-ready-btn ${youAreReady ? 'ready' : 'not-ready'}`}
            onClick={() => code && toggleReady(code)}
            onMouseUp={() => { try { (youAreReady ? sfx.readyOff() : sfx.readyOn()); } catch {} }}
            disabled={!youAreJoined || !code}
          >
            {youAreReady ? '✅ Je ne suis plus prêt' : '🎮 Je suis prêt'}
          </button>
          <p className="syncduel-ready-hint">Les deux joueurs doivent être prêts pour lancer automatiquement</p>
        </div>

        <div className="syncduel-footer">
          <button
            className="syncduel-btn-back"
            onClick={() => {
              if (code && youAreJoined) {
                leave(code);
                try { (window as any)?.gtag?.('event', 'syncduel_leave_click'); } catch {}
                try { sfx.leave(); } catch {}
              }
              navigate('/');
            }}
          >
            ← Retour
          </button>
          {state?.inGame && <div className="syncduel-status-badge">🚀 Démarrage…</div>}
        </div>
      </div>
    </div>
  );
}
